import { AchievementOverviewItem, EventType } from './achievements';

export interface ConfettiParticle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  color: string;
  rotation: number;
  rotationSpeed: number;
  tiltAngle: number;
  tiltSpeed: number;
  shape: 'rect' | 'circle' | 'star' | 'ribbon';
  opacity: number;
  life: number;
  maxLife: number;
  drag: number;
  gravity: number;
}

export interface BadgeTierInfo {
  key: 'silver' | 'gold' | 'platinum' | 'diamond' | 'ruby' | 'sapphire';
  name: string;
  emoji: string;
  gradientStops: { offset: string; color: string }[];
  accentColor: string;
  rimColor: string;
  confettiColors: string[];
}

export const BADGE_TIERS: Record<string, BadgeTierInfo> = {
  silver: {
    key: 'silver',
    name: 'Silver',
    emoji: '🥈',
    gradientStops: [
      { offset: '0%', color: '#94a3b8' },
      { offset: '50%', color: '#7e8fa4' },
      { offset: '100%', color: '#64748b' },
    ],
    accentColor: '#475569',
    rimColor: '#cbd5e1',
    confettiColors: [
      '#f8fafc',
      '#e2e8f0',
      '#cbd5e1',
      '#94a3b8',
      '#ffffff',
      '#64748b',
    ],
  },
  gold: {
    key: 'gold',
    name: 'Gold',
    emoji: '🥇',
    gradientStops: [
      { offset: '0%', color: '#f59e0b' },
      { offset: '50%', color: '#d97706' },
      { offset: '100%', color: '#b45309' },
    ],
    accentColor: '#92400e',
    rimColor: '#fbbf24',
    confettiColors: [
      '#fef08a',
      '#fde047',
      '#eab308',
      '#ca8a04',
      '#ffffff',
      '#fbbf24',
      '#b45309',
    ],
  },
  platinum: {
    key: 'platinum',
    name: 'Platinum',
    emoji: '💎',
    gradientStops: [
      { offset: '0%', color: '#10b981' },
      { offset: '50%', color: '#059669' },
      { offset: '100%', color: '#047857' },
    ],
    accentColor: '#064e3b',
    rimColor: '#34d399',
    confettiColors: [
      '#ecfdf5',
      '#a7f3d0',
      '#6ee7b7',
      '#34d399',
      '#10b981',
      '#059669',
      '#ffffff',
    ],
  },
  diamond: {
    key: 'diamond',
    name: 'Diamond',
    emoji: '💠',
    gradientStops: [
      { offset: '0%', color: '#38bdf8' },
      { offset: '50%', color: '#0ea5e9' },
      { offset: '100%', color: '#0284c7' },
    ],
    accentColor: '#0369a1',
    rimColor: '#7dd3fc',
    confettiColors: [
      '#f0f9ff',
      '#bae6fd',
      '#7dd3fc',
      '#38bdf8',
      '#0284c7',
      '#ffffff',
      '#e0f2fe',
    ],
  },
  ruby: {
    key: 'ruby',
    name: 'Ruby',
    emoji: '👑',
    gradientStops: [
      { offset: '0%', color: '#f43f5e' },
      { offset: '50%', color: '#e11d48' },
      { offset: '100%', color: '#be123c' },
    ],
    accentColor: '#9f1239',
    rimColor: '#fb7185',
    confettiColors: [
      '#ffe4e6',
      '#fb7185',
      '#f43f5e',
      '#e11d48',
      '#ffffff',
      '#fda4af',
      '#be123c',
    ],
  },
  sapphire: {
    key: 'sapphire',
    name: 'Sapphire',
    emoji: '🔷',
    gradientStops: [
      { offset: '0%', color: '#9333ea' },
      { offset: '50%', color: '#7c3aed' },
      { offset: '100%', color: '#581c87' },
    ],
    accentColor: '#3b0764',
    rimColor: '#c084fc',
    confettiColors: [
      '#f5f3ff',
      '#ddd6fe',
      '#c4b5fd',
      '#a78bfa',
      '#9333ea',
      '#7c3aed',
      '#ffffff',
      '#581c87',
    ],
  },
};

export interface AchievementSection {
  eventType: number;
  label: string;
  description: string;
  achievements: AchievementOverviewItem[];
  progress: number;
}

export const EVENT_TYPE_LABELS: Record<number, string> = {
  [EventType.REFER_LEAD]: 'Referring A Business Lead',
  [EventType.ASSIGN_LEAD]: 'Assigning A Lead',
  [EventType.UPDATE_STATUS]: "Updating A Lead's Status",
  [EventType.FUND_LEAD]: 'Funding A Lead',
  [EventType.SHARE_LEAD]: 'Sharing A Lead',
};

export const EVENT_TYPE_DESCRIPTIONS: Record<number, string> = {
  [EventType.REFER_LEAD]:
    'Earn badges by referring new business opportunities and client leads to the network.',
  [EventType.ASSIGN_LEAD]: 'Unlock milestones by assigning incoming leads.',
  [EventType.UPDATE_STATUS]:
    'Progress through tiers by keeping lead statuses actively updated.',
  [EventType.FUND_LEAD]:
    'Achieve top honours by successfully funding and closing business deals.',
  [EventType.SHARE_LEAD]:
    'Collaborate across your team and partner connections by sharing leads.',
};

export const SECTION_THEMES: Record<number, { key: string }> = {
  [EventType.REFER_LEAD]: {
    key: 'theme-blue',
  },
  [EventType.ASSIGN_LEAD]: {
    key: 'theme-cyan',
  },
  [EventType.UPDATE_STATUS]: {
    key: 'theme-orange',
  },
  [EventType.FUND_LEAD]: {
    key: 'theme-green',
  },
  [EventType.SHARE_LEAD]: {
    key: 'theme-ruby',
  },
};

export const RECENT_THRESHOLD_DAYS = 14;

export function getAchievementTierInfo(
  achievement:
    | { milestone?: number; id?: number; achievement?: { milestone?: number } }
    | number
    | null
    | undefined,
): BadgeTierInfo {
  if (!achievement) return BADGE_TIERS['silver'];
  if (typeof achievement === 'number') return BADGE_TIERS['silver'];

  let milestone = 1;
  if ('milestone' in achievement && typeof achievement.milestone === 'number') {
    milestone = achievement.milestone;
  } else if (
    'achievement' in achievement &&
    achievement.achievement &&
    typeof achievement.achievement.milestone === 'number'
  ) {
    milestone = achievement.achievement.milestone;
  }

  if (milestone <= 1) return BADGE_TIERS['silver'];
  if (milestone <= 5) return BADGE_TIERS['gold'];
  if (milestone <= 15) return BADGE_TIERS['platinum'];
  if (milestone <= 20) return BADGE_TIERS['diamond'];
  if (milestone <= 30) return BADGE_TIERS['ruby'];
  return BADGE_TIERS['sapphire'];
}

export function getAchievementIconSlug(title?: string): string {
  if (!title) return 'default';
  return title
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function getThemeConfettiColors(eventType: number): string[] {
  const goldAndWhite = ['#fbbf24', '#f59e0b', '#ffffff', '#fef08a'];
  switch (Number(eventType)) {
    case EventType.REFER_LEAD:
      return [
        '#4f72d4',
        '#334ea5',
        '#6366f1',
        '#818cf8',
        '#a5b4fc',
        ...goldAndWhite,
      ];
    case EventType.ASSIGN_LEAD:
      return [
        '#06b6d4',
        '#0891b2',
        '#0284c7',
        '#38bdf8',
        '#67e8f9',
        ...goldAndWhite,
      ];
    case EventType.UPDATE_STATUS:
      return [
        '#f97316',
        '#ea580c',
        '#fb923c',
        '#fdba74',
        '#fed7aa',
        ...goldAndWhite,
      ];
    case EventType.FUND_LEAD:
      return [
        '#10b981',
        '#059669',
        '#047857',
        '#34d399',
        '#6ee7b7',
        ...goldAndWhite,
      ];
    case EventType.SHARE_LEAD:
      return [
        '#f43f5e',
        '#e11d48',
        '#be123c',
        '#fb7185',
        '#fda4af',
        ...goldAndWhite,
      ];
    default:
      return [
        '#334ea5',
        '#6b8fe8',
        '#fbbf24',
        '#34d399',
        '#f87171',
        '#a78bfa',
        '#ffffff',
      ];
  }
}
